import CourseSupportCard from "../../Courses/CourseSupportCard";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// importing third party libraries
import { FaArrowRightLong } from "react-icons/fa6";
import { FiAward, FiCheck } from "react-icons/fi";
import { ChevronDown, CircleCheckBig, Clock3, RefreshCw, WalletCards } from "lucide-react";

// importing custom hooks
import { useThunk } from "../../../hooks/useThunk";

// importing components
import InfoCard from "../../../components/Dashboard/InforCard";
import { api } from "../../../config/api";

// importing functions, utils, slices, stores, thunks and so on..
import { currencyFormatter } from "../../../utils/formatting";
import { getCustomerDiscountName, seasonalDiscount } from "../../../utils/seasonalDiscount";

// import Tooltip from "../../components/Tooltips/InfoTootip";
import { getUserStatics } from "../../../store";
import showToast from "../../../utils/toastNotifications";
import { useSelector } from "react-redux";
import { tooltips } from "../../../constants/commonConstants";

const RewardProgress = ({ label, value, target, formatValue = (amount) => amount }) => {
  const percent = target > 0 ? Math.min(100, Math.round((Number(value || 0) / target) * 100)) : 100;
  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex items-end justify-between gap-2 font-inter text-[11px]">
        <span className="font-semibold text-secondary">{label}</span>
        <span className="shrink-0 text-gray">{formatValue(value || 0)} / {formatValue(target || 0)}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

const RewardsHub = ({ loyaltyProgress, onNavigate }) => {
  const [showAllLevels, setShowAllLevels] = useState(false);
  const current = loyaltyProgress?.currentLevel || { level: 0, name: "Standard", discountPercent: 30 };
  const next = loyaltyProgress?.nextLevel;
  const levels = loyaltyProgress?.levels || [];
  const levelItems = levels.map((level) => {
    const isCurrent = level.level === current.level;
    const isCompleted = level.level < current.level;

    return (
      <li key={level.level} className={`rounded-lg border px-2 py-1.5 text-center transition ${isCurrent ? "border-primary bg-soft-blue" : isCompleted ? "border-primary/30 bg-white" : "border-slate-200 bg-slate-50 opacity-50"}`}>
        <div className="flex items-center justify-center gap-1.5">
          <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${isCurrent || isCompleted ? "bg-primary text-white" : "bg-slate-200 text-gray"}`}>
            {isCompleted ? <FiCheck /> : level.level + 1}
          </span>
          <span className="truncate font-inter text-[10px] font-semibold text-gray" title={level.name}>{level.name}</span>
        </div>
        <p className="mt-0.5 font-inter text-xs font-bold text-primary">{level.discountPercent}%{isCurrent ? " · Current" : ""}</p>
      </li>
    );
  });

  return (
    <section className="h-full w-full rounded-[12px] bg-white shadow-[0px_8px_32px_0px_rgba(51,37,174,0.08)]" aria-labelledby="loyalty-rewards-title">
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <h2 id="loyalty-rewards-title" className="font-inter text-level-3 font-semibold text-secondary">Loyalty Rewards</h2>
          <button onClick={() => onNavigate("/loyalty-rewards")} className="inline-flex shrink-0 items-center gap-1.5 font-inter text-xs font-semibold text-primary hover:underline">
            View rewards <FaArrowRightLong className="text-[10px]" />
          </button>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-soft-blue p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-sm">
                <FiAward className="h-5 w-5" />
              </span>
              <div>
                <p className="font-inter text-xs font-semibold text-gray">Your discount</p>
                <p className="font-roboto text-2xl font-bold text-primary">{current.discountPercent}% off</p>
              </div>
            </div>
            {next && (
              <div className="rounded-lg bg-white px-3 py-2 sm:text-right">
                <p className="font-inter text-[11px] text-gray">Next reward</p>
                <p className="font-inter text-sm font-bold text-primary">{next.discountPercent}% off</p>
              </div>
            )}
          </div>

          {next ? (
            <>
              <p className="mt-4 font-inter text-[11px] font-semibold text-secondary">Unlock it with either:</p>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <RewardProgress label="Completed orders" value={loyaltyProgress.qualifyingOrders} target={next.orders} />
                <RewardProgress label="Qualifying spend" value={loyaltyProgress.qualifyingSpend} target={next.spend} formatValue={currencyFormatter.format} />
              </div>
              <p className="mt-3 font-inter text-xs leading-5 text-gray">
                <strong>{loyaltyProgress.remainingOrders}</strong> more order{loyaltyProgress.remainingOrders === 1 ? "" : "s"} or <strong>{currencyFormatter.format(loyaltyProgress.remainingSpend || 0)}</strong> more spending.
              </p>
            </>
          ) : (
            <p className="mt-3 font-inter text-xs font-semibold text-primary">You have reached the highest loyalty level.</p>
          )}
        </div>

        {levels.length > 0 && (
          <div className="mt-3">
            <button type="button" onClick={() => setShowAllLevels((visible) => !visible)} aria-expanded={showAllLevels} className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2 font-inter text-xs font-semibold text-secondary sm:hidden">
              View all levels
              <ChevronDown className={`h-4 w-4 transition-transform ${showAllLevels ? "rotate-180" : ""}`} />
            </button>
            {showAllLevels && <ol className="mt-2 grid grid-cols-2 gap-2 sm:hidden" aria-label="Loyalty levels">{levelItems}</ol>}
            <ol className="hidden grid-cols-6 gap-1.5 sm:grid" aria-label="Loyalty levels">{levelItems}</ol>
          </div>
        )}
      </div>

    </section>
  );
};

const Dashboard = () => {

  const navigate = useNavigate();
  const wallet = useSelector((state) => state.user.wallet);// wallet
  const isGetUserWallet = useSelector((state) => state.user.isWalletFetching); //wallet fetching state
  
  // defining local states ========================================================================================
  const [userStatics, setUserStatics] = useState(null);
  const [loyaltyProgress, setLoyaltyProgress] = useState(null);

  // defining custom hooks ========================================================================================
  const [doGetUserStatics, isGetUserStatics, errorGetUserStatics] = useThunk(getUserStatics);
  
  // defining side effects ========================================================================================
  useEffect(() => {
    fetchUserStaticsMethod();
  }, [])

  // defining functions ===========================================================================================

  // Fetch dashboard and loyalty information independently.
  const fetchUserStaticsMethod = async() => {
    const loyaltyPromise = api.get("/loyalty/me").then((response) => {
      setLoyaltyProgress(response.data.data);
    }).catch(() => {});

    const staticsPromise = doGetUserStatics().then((result) => {
      if (result?.success) {
        const statics = result?.response?.data?.[0] || null;
        setUserStatics(statics);
        return;
      }

      showToast("error", result?.error?.message || "Error Occured");
    });

    await Promise.allSettled([staticsPromise, loyaltyPromise]);
  }

  // derived information =========================================================================================
  const loyaltyDiscountPercent = Number(loyaltyProgress?.currentLevel?.discountPercent || 0);
  const hasUpgradedLoyaltyDiscount = loyaltyProgress?.enabled && loyaltyDiscountPercent > seasonalDiscount.percent;
  const activeDiscountPercent = hasUpgradedLoyaltyDiscount ? loyaltyDiscountPercent : seasonalDiscount.percent;
  const activeDiscountName = getCustomerDiscountName(loyaltyProgress);

  return (
    <div
      className={`min-[80%] mt-24 xl:mt-[100px] px-4 xl:px-8 flex flex-col w-full xl:w-[calc(100vw-256px)] h-full`}
    >
      <div className="bg-[#F8F8F8] px-6 py-[30px] space-y-[24px] mb-[32px] xl:mt-0 rounded-[12px] flex flex-col">
        {/* First Row */}
        <div className="grid sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-4 gap-4">
          <InfoCard 
            title="Wallet Balance" 
            value={currencyFormatter.format(wallet?.amount || 0) || "$ 0.00"}
            cardIcon={WalletCards}
            bgColor="rgba(255,144,102,0.21)"
            onClick={() => navigate("/my-wallet")}
            loading={isGetUserWallet}
            error={errorGetUserStatics} 
          />
          <InfoCard 
            title="Pending Amount" 
            tooltip={tooltips.PENDING_AMOUNT}
            value={userStatics?.totalPendingAmount ? userStatics?.totalPendingAmount[0] ? currencyFormatter.format(userStatics?.totalPendingAmount[0].totalAmount || 0) || '$ 0.00' : '$ 0.00' : '$ 0.00'}
            cardIcon={Clock3}
            bgColor="rgba(255,144,102,0.21)"
            onClick={() => navigate("/in-progress")}
            loading={isGetUserStatics}
            error={errorGetUserStatics} 
          />
          <InfoCard
            title="Completed Orders"
            value={userStatics?.totalCompletedOrders ? userStatics?.totalCompletedOrders[0] ? userStatics?.totalCompletedOrders[0].count || '' : '0' : ''}
            cardIcon={CircleCheckBig}
            bgColor="rgba(255,144,102,0.21)"
            onClick={() => navigate("/raise-complaint")}
            loading={isGetUserStatics}
            error={errorGetUserStatics}
          />
          <InfoCard
            title="Ongoing Orders"
            value={userStatics?.totalActiveOrdersInProgress ? userStatics?.totalActiveOrdersInProgress[0] ? userStatics?.totalActiveOrdersInProgress[0].count || '' : '0' : ''}
            cardIcon={RefreshCw}
            bgColor="rgba(255,144,102,0.21)"
            onClick={() => navigate("/in-progress")}
            loading={isGetUserStatics}
            error={errorGetUserStatics}
          />
        </div>

        {seasonalDiscount.enabled && (
          <div className="flex flex-col gap-3 rounded-[12px] border border-[#CDD8FF] bg-gradient-to-r from-[#F4F7FF] via-white to-[#EEF3FF] px-4 py-3 shadow-[0_12px_34px_rgba(0,32,189,0.08)] sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0020BD] text-[12px] font-bold text-white shadow-[0_6px_14px_rgba(0,32,189,0.22)]">
                {activeDiscountPercent}%
              </span>
              <div>
                <p className="text-[14px] font-semibold leading-5 text-[#00178A]">
                  Your {activeDiscountPercent}% {activeDiscountName} is active
                </p>
                <p className="text-[13px] font-medium leading-5 text-[#5d6b8c]">
                  Applied automatically to eligible orders.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/calculation")}
              className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-[8px] bg-[#0020BD] px-4 text-[13px] font-semibold text-white shadow-[0_10px_20px_rgba(0,32,189,0.18)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#00178A]"
            >
              Order now
              <FaArrowRightLong className="text-[12px]" />
            </button>
          </div>
        )}

        {/* Full-width course support row */}
        <CourseSupportCard dashboard />

        {/* Full-width loyalty rewards row */}
        {loyaltyProgress?.enabled && (
          <RewardsHub
            loyaltyProgress={loyaltyProgress}
            onNavigate={navigate}
          />
        )}

      </div>
    </div>
  );
};

export default Dashboard;
